import assertNever from '../utils/assertNever';
import { ASTExpr } from './ast';

export function namespaceVariable(
  path: ReadonlyArray<string>,
  variable: string
): string {
  // Namespaces are backwards from how we normally treat them in engineering.
  // For instance, `foo.bar.baz` (namespacing `baz` with the path [foo, bar])
  // becomes `baz@bar@foo`. This has a few benefits:
  // - It scans better (consider `emissions@electricity@weaving`)
  // - The thing the user typed in is first
  // The main downside is that it is extremely strange
  for (let i = path.length - 1; i >= 0; i--) {
    variable += '@' + path[i];
  }
  return variable;
}

// Recursively rewrite an expression (postorder traversal) by calling the given
// rewrite function on each node of the AST. If you are targeting nodes of a
// specific sort, try rewriteExprType below!
export function rewriteExpr(
  expr: ASTExpr,
  rewrite: (e: ASTExpr) => ASTExpr
): ASTExpr {
  switch (expr.t) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'identifier':
    case 'null':
      return rewrite(expr);
    case 'unary': {
      return rewrite({
        t: 'unary',
        expr: rewriteExpr(expr.expr, rewrite),
        op: expr.op,
      });
    }
    case 'binary': {
      return rewrite({
        t: 'binary',
        op: expr.op,
        l: rewriteExpr(expr.l, rewrite),
        r: rewriteExpr(expr.r, rewrite),
      });
    }
    case 'comparison': {
      return rewrite({
        t: 'comparison',
        op: expr.op,
        l: rewriteExpr(expr.l, rewrite),
        r: rewriteExpr(expr.r, rewrite),
      });
    }
    case 'call':
      return rewrite({
        t: 'call',
        name: expr.name,
        args: expr.args.map((e) => rewriteExpr(e, rewrite)),
        kwargs: new Map(
          Array.from(expr.kwargs).map(([k, { t, expr: v }]) => [
            k,
            { t, expr: rewriteExpr(v, rewrite) },
          ])
        ),
      });
    default:
      assertNever(expr);
  }
}

// Recursively rewrite a specific AST node type in an AST.
export function rewriteExprType<T extends ASTExpr>(
  expr: ASTExpr,
  t: T['t'],
  rewrite: (e: T) => ASTExpr
): ASTExpr {
  return rewriteExpr(expr, (e) => {
    if (e.t !== t) {
      return e;
    }
    return rewrite(e as T);
  });
}
